import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { RefreshCw, Radio } from "lucide-react";

const ENDPOINT_OPTIONS = [
  { value: "", label: "All endpoints" },
  { value: "/link/knowledge", label: "/link/knowledge" },
  { value: "/link/customers", label: "/link/customers" },
];

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

export default function AdminCrossHubAccess() {
  const [rows, setRows] = useState([]);
  const [participant, setParticipant] = useState("");
  const [endpoint, setEndpoint] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200, _: Date.now() };
      if (participant.trim()) params.participant = participant.trim();
      if (endpoint) params.endpoint = endpoint;
      const r = await api.get("/admin/link/access-log", { params });
      setRows(r.data.rows || []);
      setRefreshedAt(new Date());
    } finally {
      setLoading(false);
    }
  }, [participant, endpoint]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = useMemo(() => {
    const total = rows.length;
    const byEndpoint = rows.reduce((acc, r) => {
      acc[r.endpoint] = (acc[r.endpoint] || 0) + 1;
      return acc;
    }, {});
    return { total, byEndpoint };
  }, [rows]);

  return (
    <div className="space-y-6">
      <div
        data-testid="cross-hub-header"
        className="rounded-2xl border border-zinc-800/80 bg-zinc-950/40 p-5 grain relative overflow-hidden"
      >
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-500/50 to-transparent" />
        <div className="flex items-center gap-2">
          <Radio className="w-3.5 h-3.5 text-emerald-400" />
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
            Cross-hub access
          </div>
        </div>
        <div className="mt-2 text-sm text-zinc-400 max-w-2xl">
          The last 200 calls made by the AI Office to this hub&apos;s participant
          data endpoints. Read-only. Use it to see exactly what the agents have
          fetched, and for which member.
        </div>
        <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-zinc-400">
          <span
            data-testid="cross-hub-total"
            className="px-2 py-1 rounded border border-zinc-800 bg-zinc-950/50"
          >
            {summary.total} rows shown
          </span>
          {Object.entries(summary.byEndpoint).map(([ep, n]) => (
            <span
              key={ep}
              data-testid={`cross-hub-count-${ep.replace(/\W+/g, "-")}`}
              className="px-2 py-1 rounded border border-emerald-500/20 text-emerald-300"
            >
              {ep} · {n}
            </span>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          data-testid="cross-hub-filter-participant"
          value={participant}
          onChange={(e) => setParticipant(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") load();
          }}
          placeholder="Filter by participant email"
          className="min-w-[260px] px-3 py-2 rounded-lg text-sm bg-zinc-950/60 border border-zinc-800 text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500/50"
        />
        <select
          data-testid="cross-hub-filter-endpoint"
          value={endpoint}
          onChange={(e) => setEndpoint(e.target.value)}
          className="px-3 py-2 rounded-lg text-sm bg-zinc-950/60 border border-zinc-800 text-zinc-200 focus:outline-none focus:border-emerald-500/50"
        >
          {ENDPOINT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <button
          data-testid="cross-hub-apply-btn"
          onClick={load}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs uppercase tracking-[0.18em] border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 transition-colors"
        >
          Apply
        </button>
        <div className="ml-auto flex items-center gap-2">
          <button
            data-testid="cross-hub-refresh-btn"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs uppercase tracking-[0.18em] border border-zinc-800 bg-zinc-950/60 text-zinc-300 hover:text-emerald-300 hover:border-emerald-500/30 transition-colors disabled:opacity-50"
            title={refreshedAt ? `Last refreshed: ${refreshedAt.toLocaleTimeString()}` : "Refresh"}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Table */}
      <div
        data-testid="cross-hub-table"
        className="border border-zinc-900 rounded-2xl bg-zinc-950/40 overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/40 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              <tr>
                <th className="text-left px-4 py-3">When</th>
                <th className="text-left px-4 py-3">Participant</th>
                <th className="text-left px-4 py-3">Endpoint</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Caller</th>
                <th className="text-right px-4 py-3">Results</th>
                <th className="text-left px-4 py-3 hidden lg:table-cell">Params</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`cross-hub-row-${r.id}`}
                  className="border-t border-zinc-900 hover:bg-zinc-900/30"
                >
                  <td className="px-4 py-3 font-mono text-xs text-zinc-400 whitespace-nowrap">
                    {fmtTime(r.at)}
                  </td>
                  <td className="px-4 py-3 text-zinc-200 text-xs">
                    {r.participant_email || "—"}
                  </td>
                  <td className="px-4 py-3 text-emerald-300 text-xs font-mono">
                    {r.endpoint}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell font-mono text-[11px] text-zinc-500">
                    {r.caller || "—"}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-xs text-amber-300">
                    {Number(r.result_count ?? 0)}
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell font-mono text-[11px] text-zinc-500 max-w-[280px] truncate">
                    {r.params ? JSON.stringify(r.params) : "—"}
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-zinc-500">
                    No cross-hub access yet.
                  </td>
                </tr>
              )}
              {loading && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-zinc-500">
                    Loading…
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
